import { Children, isValidElement, ReactElement, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Icon as BaseIcon } from "@sellar-io/components";
import { IconType } from "@sellar-io/components";
import { useMenuDrawerContext } from "./MenuDrawerContextProvider.shared";
import { NavigationListElementBase } from "./MenuDrawerListElementBase";
import { NavigationListElementBaseProps } from "./MenuDrawerListElementBase.shared";

export type MenuDrawerListElementLabelProps = {
  children: ReactNode;
  className?: string;
};

const Label = ({ children, className }: MenuDrawerListElementLabelProps) => {
  const { isRailDisplayType } = useMenuDrawerContext();

  if (isRailDisplayType) return null;

  return (
    <label
      className={twMerge(
        "cursor-pointer truncate font-semibold whitespace-nowrap",
        isRailDisplayType && "hidden",
        className,
      )}
    >
      {children}
    </label>
  );
};

const getLabelContent = (children: ReactNode) => {
  const label = Children.toArray(children).find(
    (
      child,
    ): child is ReactElement<MenuDrawerListElementLabelProps, typeof Label> =>
      isValidElement(child) && child.type === Label,
  );

  return label?.props.children;
};

const NavigationListElementRoot = (
  props: NavigationListElementBaseProps,
): ReactElement => {
  return (
    <NavigationListElementBase
      {...props}
      tooltipContent={getLabelContent(props.children)}
      className={twMerge(
        "my-0.5 flex cursor-pointer items-center gap-2 space-x-2 rounded-lg px-2 py-3 hover:bg-gray-100",
        props.className,
      )}
    />
  );
};

export type MenuDrawerListElementIconProps = {
  icon: keyof typeof IconType;
  size?: number;
  count?: {
    className: string;
    value: number;
  };
};

const Icon = ({ icon, size, count }: MenuDrawerListElementIconProps) => {
  // Keep menu icons in a fixed square so different glyph metrics don't shift label alignment.
  return (
    <div className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center">
      <BaseIcon icon={icon} size={size ?? 1} />
      {count && count.value > 0 && (
        <span
          className={twMerge(
            "absolute -top-2 -right-2 flex min-h-4 min-w-4 items-center justify-center rounded-full p-0.5 text-xs font-semibold",
            count.className,
          )}
        >
          {count.value}
        </span>
      )}
    </div>
  );
};

const Section = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return <div className={twMerge("flex flex-col", className)}>{children}</div>;
};

export {
  NavigationListElementRoot as MenuDrawerListElement,
  Icon as MenuDrawerListElementIcon,
  Label as MenuDrawerListElementLabel,
  Section as MenuDrawerListElementSection,
};
